/**
 * Supabase Client Configuration
 *
 * Supports two modes:
 * 1. Development Mode (no Supabase): Uses mock data from stores
 * 2. Production Mode (with Supabase): Full database connectivity
 *
 * The app works without Supabase credentials - stores fall back to local state.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '~/types/database.types';

// ============================================================================
// ENVIRONMENT VALIDATION
// ============================================================================

const supabaseUrl = import.meta.env['VITE_SUPABASE_URL'] as string | undefined;
const supabaseAnonKey = import.meta.env['VITE_SUPABASE_ANON_KEY'] as string | undefined;

/**
 * Check if Supabase is configured
 * Returns false if env vars are missing or placeholder values
 */
export function isSupabaseConfigured(): boolean {
  if (!supabaseUrl || !supabaseAnonKey) return false;
  if (supabaseUrl.includes('your-project-id')) return false;
  if (supabaseAnonKey.includes('your-anon-key')) return false;
  return true;
}

// ============================================================================
// SUPABASE CLIENT
// ============================================================================

/**
 * Create a new Supabase client instance
 */
function createSupabaseClient(): SupabaseClient<Database> | null {
  if (!isSupabaseConfigured()) return null;

  return createClient<Database>(supabaseUrl!, supabaseAnonKey!, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      // ISSUE_073: navigator.locks 교착 방지 — no-op lock
      //
      // SDK 기본값은 navigatorLock (Web Lock API, exclusive mode).
      // 백그라운드 탭 복귀 시 _onVisibilityChanged() → _recoverAndRefresh()가
      // 이 lock을 잡고 네트워크 요청을 보내는 동안,
      // 모든 REST 쿼리의 getSession()이 같은 lock 대기 → 30초 타임아웃 → NETWORK_ERROR 폭주.
      //
      // Tauri 앱(단일 탭)이므로 크로스-탭 세션 동기화 불필요.
      // No-op lock으로 교체하면 visibility 복귀 시에도 쿼리가 즉시 실행됨.
      lock: async <R>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => {
        return await fn();
      },
    },
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
      // Heartbeat to keep connection alive (especially for Safari)
      heartbeatIntervalMs: 15000,
      // Reconnect settings
      reconnectAfterMs: (tries: number) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
        return Math.min(1000 * Math.pow(2, tries), 30000);
      },
    },
  });
}

/**
 * Supabase client instance
 * - Returns null if not configured (dev mode)
 * - Returns typed client if configured (production mode)
 * - Can be reset via resetSupabaseClient() for SDK deadlock recovery
 */
// eslint-disable-next-line import/no-mutable-exports
export let supabase: SupabaseClient<Database> | null = createSupabaseClient();


/**
 * ISSUE_047: Reset Supabase client to recover from SDK deadlock
 * Session is preserved in localStorage, new client will auto-restore it
 */
export function resetSupabaseClient(): SupabaseClient<Database> | null {
  // Disconnect existing realtime connections
  if (supabase) {
    try {
      supabase.realtime.disconnect();
    } catch {
      // Ignore disconnect errors during reset
    }
  }

  // Create fresh client (session auto-restored from localStorage)
  supabase = createSupabaseClient();

  return supabase;
}

/**
 * ISSUE_047: Ping Supabase REST API directly (bypassing SDK)
 * Used to detect SDK deadlock vs actual network issues
 */
export async function pingSupabase(): Promise<boolean> {
  if (!supabaseUrl || !supabaseAnonKey) return false;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    const res = await fetch(`${supabaseUrl}/rest/v1/`, {
      method: 'HEAD',
      headers: {
        'apikey': supabaseAnonKey,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return res.ok || res.status === 400 || res.status === 401; // 4xx = server reachable
  } catch {
    return false;
  }
}

// ============================================================================
// RAW REFRESH (ISSUE_161 Phase C-2)
// ============================================================================

/**
 * ISSUE_161 Phase C-2: gotrue refresh 엔드포인트 raw fetch.
 *
 * SDK 의 `sb.auth.refreshSession()` 은 initializePromise → _acquireLock
 * → retryable(200~800ms backoff) → _request 체인을 거치며 수 초를 먹을 수 있음.
 * 게다가 이 체인이 `setTimeout(10s)` wall-clock 과 따로 돌아서 "timer 는
 * 만료됐는데 실제 fetch 는 발행 전" 인 상황 발생 (ζ-2 이벤트 루프 밀림).
 *
 * 이 헬퍼는 AbortController + 명시 타이머로 SDK 오버헤드를 제거하고
 * enqueue/dispatch/first-byte 세 지점을 performance.now() 로 계측.
 */

export interface RawRefreshTiming {
  /** 함수 진입 직후 */
  enqueuedAt: number;
  /** fetch() 호출 직전. null 이면 fetch 발행 전에 abort/offline */
  dispatchedAt: number | null;
  /** response headers 수신 시점. null 이면 응답 미도달 */
  firstByteAt: number | null;
  /** 함수 return/throw 직전 */
  endedAt: number;
}

export type RawRefreshErrorKind =
  | 'timeout'
  | 'network'
  | 'refresh_token_expired'
  | 'http'
  | 'parse'
  | 'offline';

export interface RawRefreshResult {
  ok: boolean;
  status: number | null;
  aborted: boolean;
  errorKind: RawRefreshErrorKind | null;
  errorMessage: string | null;
  session: {
    access_token: string;
    refresh_token: string;
    expires_at: number;
    expires_in: number;
    token_type: string;
  } | null;
  timing: RawRefreshTiming;
}

/**
 * gotrue `/auth/v1/token?grant_type=refresh_token` 을 직접 호출.
 * SDK 내부 경로 완전 우회. timeout 시 AbortController 로 실제 요청 취소.
 */
export async function refreshSessionRaw(
  refreshToken: string,
  timeoutMs: number,
): Promise<RawRefreshResult> {
  const enqueuedAt = performance.now();
  let dispatchedAt: number | null = null;
  let firstByteAt: number | null = null;

  const finish = (
    partial: Omit<RawRefreshResult, 'timing'>,
  ): RawRefreshResult => ({
    ...partial,
    timing: { enqueuedAt, dispatchedAt, firstByteAt, endedAt: performance.now() },
  });

  if (!supabaseUrl || !supabaseAnonKey) {
    return finish({
      ok: false, status: null, aborted: false,
      errorKind: 'offline', errorMessage: 'Supabase not configured', session: null,
    });
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return finish({
      ok: false, status: null, aborted: false,
      errorKind: 'offline', errorMessage: 'navigator.onLine=false', session: null,
    });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    dispatchedAt = performance.now();
    const res = await fetch(
      `${supabaseUrl}/auth/v1/token?grant_type=refresh_token`,
      {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      },
    );
    firstByteAt = performance.now();
    clearTimeout(timeoutId);

    if (!res.ok) {
      // gotrue 는 refresh_token 만료 시 400 또는 403 + body 에 error_description
      const text = await res.text().catch(() => '');
      const lower = text.toLowerCase();
      const isExpired =
        res.status === 400 || res.status === 403 ||
        lower.includes('refresh_token') ||
        lower.includes('invalid refresh token') ||
        lower.includes('token has expired') ||
        lower.includes('already used') ||
        lower.includes('session_not_found') ||
        lower.includes('session missing');
      return finish({
        ok: false, status: res.status, aborted: false,
        errorKind: isExpired ? 'refresh_token_expired' : 'http',
        errorMessage: text.slice(0, 200) || `HTTP ${res.status}`,
        session: null,
      });
    }

    let json: {
      access_token?: string;
      refresh_token?: string;
      expires_at?: number;
      expires_in?: number;
      token_type?: string;
    };
    try {
      json = await res.json();
    } catch (err) {
      return finish({
        ok: false, status: res.status, aborted: false,
        errorKind: 'parse', errorMessage: (err as Error).message, session: null,
      });
    }

    if (!json.access_token || !json.refresh_token) {
      return finish({
        ok: false, status: res.status, aborted: false,
        errorKind: 'parse', errorMessage: 'missing tokens in response', session: null,
      });
    }

    const expiresIn = json.expires_in ?? 3600;
    const expiresAt = json.expires_at ?? Math.floor(Date.now() / 1000) + expiresIn;
    return finish({
      ok: true, status: res.status, aborted: false,
      errorKind: null, errorMessage: null,
      session: {
        access_token: json.access_token,
        refresh_token: json.refresh_token,
        expires_at: expiresAt,
        expires_in: expiresIn,
        token_type: json.token_type ?? 'bearer',
      },
    });
  } catch (err) {
    clearTimeout(timeoutId);
    const isAbort = err instanceof DOMException && err.name === 'AbortError';
    return finish({
      ok: false, status: null, aborted: isAbort,
      errorKind: isAbort ? 'timeout' : 'network',
      errorMessage: (err as Error).message,
      session: null,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

// ============================================================================
// CONNECTION TEST
// ============================================================================

/**
 * Test the Supabase connection by fetching from a table
 * Call this on app initialization to verify connectivity
 */
export async function testConnection(): Promise<boolean> {
  if (!supabase) {
    return false;
  }

  try {
    // Try to fetch from students table (should exist after schema.sql)
    const { error } = await supabase
      .from('students')
      .select('id')
      .limit(1);

    if (error) {
      console.error('🔴 [Supabase] Connection FAILED:', error.message);
      console.error('   Details:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('🔴 [Supabase] Connection error:', err);
    return false;
  }
}

// Auto-test connection on module load (development only)
if (import.meta.env.DEV && supabase) {
  testConnection();
}

// ============================================================================
// AUTH HELPERS
// ============================================================================

/**
 * Get the current session
 */
export async function getSession() {
  if (!supabase) return null;

  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('[Supabase] Error getting session:', error);
    return null;
  }
  return session;
}

/**
 * Get the current user
 */
export async function getCurrentUser() {
  if (!supabase) return null;

  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    console.error('[Supabase] Error getting user:', error);
    return null;
  }
  return user;
}

/**
 * Sign out the current user
 */
export async function signOut() {
  if (!supabase) return;

  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error('[Supabase] Error signing out:', error);
    throw error;
  }
}

/**
 * ISSUE_047: Refresh the session and return new access token
 * Used by sync worker when JWT expires
 */
export async function refreshSession(): Promise<string | null> {
  if (!supabase) return null;

  const { data, error } = await supabase.auth.refreshSession();
  if (error) {
    console.error('[Supabase] Error refreshing session:', error);
    return null;
  }

  if (!data.session?.access_token) {
    console.error('[Supabase] No access token in refreshed session');
    return null;
  }

  return data.session.access_token;
}

// ============================================================================
// REST CLIENT PATCH — SDK auth 파이프라인 우회
// ============================================================================

/**
 * ISSUE_073: PostgREST client의 fetch를 직접 교체하여 SDK auth 파이프라인 완전 우회.
 *
 * 문제: supabase.from().select() → fetchWithAuth → _getAccessToken() → getSession()
 *       → initializePromise 대기 → lock 획득 → __loadSession() (localStorage 읽기 + 만료 시 refresh)
 *       이 전체 파이프라인이 탭 백그라운드 복귀 시 교착/지연을 일으킴.
 *
 * 해결: PostgREST client의 fetch 함수를 교체하여 AuthStore에서 직접 토큰을 읽음.
 *       SDK의 lock, initializePromise, __loadSession 모두 우회.
 *       Auth 클라이언트(login/logout/onAuthStateChange)는 영향 없음.
 *
 * @param getToken AuthStore에서 access_token을 읽는 함수
 */
export function patchRestClientFetch(getToken: () => string | null): void {
  if (!supabase || !supabaseAnonKey) return;

  const anonKey = supabaseAnonKey;
  // SupabaseClient 의 내부 PostgrestClient(protected)에 fetch 를 주입(토큰 갱신). 스키마 타입과
  // 무관한 SDK 내부 접근이라 좁은 형태 캐스팅으로 접근(전체 as any 아님).
  const rest = (supabase as unknown as { rest?: { fetch: typeof globalThis.fetch } }).rest;
  if (!rest) return;

  rest.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const token = getToken() ?? anonKey;
    const headers = new Headers(init?.headers);
    if (!headers.has('apikey')) headers.set('apikey', anonKey);
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return globalThis.fetch(input, { ...init, headers });
  };

}

// ============================================================================
// EDGE FUNCTION 직접 호출 — 자족적 세션 획득
// ============================================================================

/**
 * ISSUE_118: Edge Function을 raw fetch로 호출.
 *
 * REST 쿼리는 IndexedDB 캐시(Read-Through Cache)를 통해 동작하므로
 * 토큰이 만료되어도 정상 동작한다. 하지만 Edge Function은 반드시
 * Supabase Gateway를 통과해야 하므로 유효한 JWT가 필수.
 *
 * 토큰 획득 전략 (2단계):
 * 1. AuthStore 세션의 expires_at 확인 — 5분 이상 남으면 기존 토큰 재사용
 *    (refreshSession()의 navigator.locks 경합 위험 방지)
 * 2. 만료 임박 또는 세션 없음 → SDK refreshSession()으로 갱신
 *
 * AuthStore 토큰이 SDK보다 약간 뒤처질 수 있지만, 5분 마진이 있으므로
 * Edge Function 호출 시점에 토큰이 여전히 유효함이 보장된다.
 */
export async function invokeEdgeFunction<T = unknown>(name: string, body: unknown): Promise<T> {
  if (!supabaseUrl || !supabaseAnonKey || !supabase) {
    throw new Error("Supabase not configured");
  }

  // AuthStore 세션의 expires_at 확인 — 5분 이상 남으면 기존 토큰 재사용
  // refreshSession()은 navigator.locks 경합 위험이 있으므로 불필요한 호출 방지
  let token: string;
  const { useAuthStore } = await import('~/core/store/useAuthStore');
  const currentSession = useAuthStore.getState().session;
  const TOKEN_MARGIN = 5 * 60 * 1000; // 5분

  if (
    currentSession?.access_token &&
    currentSession.expires_at &&
    currentSession.expires_at * 1000 - Date.now() > TOKEN_MARGIN
  ) {
    token = currentSession.access_token;
  } else {
    // 만료 임박 또는 세션 없음 → SDK에서 세션 갱신
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshData.session?.access_token) {
      throw new Error("로그인이 필요합니다. 새로고침 후 다시 시도해주세요.");
    }
    token = refreshData.session.access_token;
  }

  // Raw fetch — SDK functions.invoke 우회
  const res = await globalThis.fetch(`${supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    const main = err.error || err.message || `Edge Function error: ${res.status}`;
    const detail = err.detail && err.detail !== main ? `: ${err.detail}` : "";
    throw new Error(`${main}${detail}`);
  }

  return res.json() as Promise<T>;
}

// ============================================================================
// EDGE FUNCTION SSE 스트리밍 — ink-agent 등
// ============================================================================

interface StreamCallbacks {
  /** SSE data 이벤트마다 호출 (파싱된 JSON) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChunk: (data: any) => void;
  /** 에러 발생 시 */
  onError?: (error: string) => void;
  /** AbortSignal (사용자 취소) */
  signal?: AbortSignal;
}

/**
 * Edge Function을 SSE 스트리밍으로 호출.
 * invokeEdgeFunction과 동일한 토큰 전략 사용.
 */
export async function invokeEdgeFunctionStream(
  name: string,
  body: unknown,
  callbacks: StreamCallbacks,
): Promise<void> {
  if (!supabaseUrl || !supabaseAnonKey || !supabase) {
    throw new Error("Supabase not configured");
  }

  // 토큰 획득 (invokeEdgeFunction과 동일)
  let token: string;
  const { useAuthStore } = await import('~/core/store/useAuthStore');
  const currentSession = useAuthStore.getState().session;
  const TOKEN_MARGIN = 5 * 60 * 1000;

  if (
    currentSession?.access_token &&
    currentSession.expires_at &&
    currentSession.expires_at * 1000 - Date.now() > TOKEN_MARGIN
  ) {
    token = currentSession.access_token;
  } else {
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshData.session?.access_token) {
      throw new Error("로그인이 필요합니다. 새로고침 후 다시 시도해주세요.");
    }
    token = refreshData.session.access_token;
  }

  const res = await globalThis.fetch(`${supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify(body),
    signal: callbacks.signal ?? null,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    const msg = err.error || err.message || `Edge Function error: ${res.status}`;
    callbacks.onError?.(msg);
    throw new Error(msg);
  }

  // SSE 파싱
  const reader = res.body?.getReader();
  if (!reader) {
    throw new Error("Response body is not readable");
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE 이벤트 파싱: data: {...}\n\n
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith('data: ')) {
          const jsonStr = trimmed.slice(6);
          if (jsonStr === '[DONE]') return;
          try {
            const data = JSON.parse(jsonStr);
            callbacks.onChunk(data);
          } catch {
            // JSON 파싱 실패 — 무시
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export default supabase;
